import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ImagePlus, Lock } from "lucide-react";
import { api, errMsg, money } from "../lib/api";
import { useLang, svcName, useTitle } from "../lib/i18n";
import { useConfig } from "../lib/config";
import { PageHead } from "../components/site/SiteLayout";
import SlotPicker, { fmtTime } from "../components/booking/SlotPicker";

export function Quote() {
  const { t, lang } = useLang();
  const cfg = useConfig();
  useTitle(t.quote.title);
  const [params] = useSearchParams();
  const [f, setF] = useState({ service_id: params.get("service") || "", name: "", email: "", phone: "", address: "", city: "Hialeah", zip: "", property_type: "house", bedrooms: 0, bathrooms: 0, sqft: 0, description: "", preferred_date: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.service_id) return toast.error(t.book.required);
    setBusy(true);
    const fd = new FormData();
    Object.entries({ ...f, lang }).forEach(([k, v]) => fd.append(k, v));
    files.forEach((x) => fd.append("photos", x));
    try { await api.post("/quote-requests", fd); setDone(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) return <div className="max-w-xl mx-auto py-24 px-4 text-center" data-testid="quote-success"><CheckCircle2 className="mx-auto text-emerald-600" size={48} /><p className="mt-4 text-lg font-semibold">{t.quote.sent}</p><Link to="/" className="btn-outline mt-6">{t.nav.home}</Link></div>;
  return (
    <div>
      <PageHead title={t.quote.title} sub={t.quote.sub} />
      <form onSubmit={submit} className="max-w-3xl mx-auto my-12 mx-4 sm:mx-auto bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-md space-y-5" data-testid="quote-form">
        <div><label className="label">{t.book.service} *</label>
          <select className="field" value={f.service_id} onChange={set("service_id")} data-testid="quote-service-select">
            <option value="">—</option>
            {(cfg?.services || []).map((s) => <option key={s.id} value={s.id}>{svcName(s, lang)}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3"><label className="label">{t.book.name} *</label><input required className="field" value={f.name} onChange={set("name")} data-testid="quote-name-input" /></div>
          <div className="sm:col-span-2"><label className="label">{t.book.email} *</label><input required type="email" className="field" value={f.email} onChange={set("email")} data-testid="quote-email-input" /></div>
          <div><label className="label">{t.book.phone} *</label><input required className="field" value={f.phone} onChange={set("phone")} data-testid="quote-phone-input" /></div>
          <div className="sm:col-span-3"><label className="label">{t.book.address} *</label><input required className="field" value={f.address} onChange={set("address")} data-testid="quote-address-input" /></div>
          <div className="sm:col-span-2"><label className="label">{t.book.city} *</label><input required className="field" value={f.city} onChange={set("city")} data-testid="quote-city-input" /></div>
          <div><label className="label">{t.book.zip} *</label><input required maxLength={5} className="field" value={f.zip} onChange={set("zip")} data-testid="quote-zip-input" /></div>
          <div><label className="label">{t.book.bedrooms}</label><input type="number" min={0} className="field" value={f.bedrooms} onChange={set("bedrooms")} data-testid="quote-bedrooms-input" /></div>
          <div><label className="label">{t.book.bathrooms}</label><input type="number" min={0} step={0.5} className="field" value={f.bathrooms} onChange={set("bathrooms")} data-testid="quote-bathrooms-input" /></div>
          <div><label className="label">{t.book.sqft}</label><input type="number" min={0} className="field" value={f.sqft} onChange={set("sqft")} data-testid="quote-sqft-input" /></div>
        </div>
        <div><label className="label">{t.quote.describe} *</label><textarea required minLength={10} rows={5} className="field" value={f.description} onChange={set("description")} data-testid="quote-description-input" /></div>
        <div><label className="label">{t.quote.preferred}</label><input type="date" className="field" value={f.preferred_date} onChange={set("preferred_date")} data-testid="quote-date-input" /></div>
        <div>
          <label className="label">{t.quote.photos}</label>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl p-6 cursor-pointer hover:border-sky-400 text-slate-500">
            <ImagePlus size={20} /> JPG, PNG, WEBP
            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple className="hidden" data-testid="custom-quote-file-input"
              onChange={(e) => setFiles([...files, ...Array.from(e.target.files)].slice(0, 6))} />
          </label>
          {files.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="quote-photo-previews">
              {files.map((x, i) => (
                <button type="button" key={i} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="relative group">
                  <img src={URL.createObjectURL(x)} alt="" className="h-20 w-full object-cover rounded-lg" />
                  <XCircle size={18} className="absolute top-1 right-1 text-white drop-shadow opacity-0 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          )}
        </div>
        <button className="btn-primary w-full" disabled={busy} data-testid="quote-submit-button">{busy && <Loader2 size={16} className="animate-spin" />}{t.quote.submit}</button>
      </form>
    </div>
  );
}

const Badge = ({ s }) => {
  const { t } = useLang();
  const c = { confirmed: "bg-emerald-100 text-emerald-800", assigned: "bg-emerald-100 text-emerald-800", completed: "bg-slate-200 text-slate-800", in_progress: "bg-sky-100 text-sky-800", cancelled: "bg-red-100 text-red-700", expired: "bg-red-100 text-red-700" }[s] || "bg-amber-100 text-amber-800";
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c}`} data-testid="booking-status-badge">{t.status[s] || s}</span>;
};

export function BookingView() {
  const { t, lang } = useLang();
  useTitle(t.view.title);
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [b, setB] = useState(null);
  const [resched, setResched] = useState(false);
  const [hold, setHold] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/bookings/${id}?token=${token}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const pay = () => act(async () => { window.location.href = (await api.post(`/bookings/${id}/pay-balance`, { token })).data.checkout_url; });
  const cancel = () => window.confirm(t.view.confirmCancel) && act(async () => { await api.post(`/bookings/${id}/cancel`, { token }); await load(); });
  const saveResched = () => act(async () => { await api.post(`/bookings/${id}/reschedule`, { token, hold_id: hold.hold_id }); setResched(false); setHold(null); await load(); });
  if (!b) return <div className="p-20 grid place-items-center"><Loader2 className="animate-spin text-sky-600" /></div>;
  const changeable = ["pending_payment", "confirmed", "assigned", "needs_scheduling"].includes(b.status);
  return (
    <div className="max-w-3xl mx-auto px-4 py-12 space-y-6" data-testid="booking-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="eyebrow">{b.number}</p><h1 className="text-4xl font-bold text-slate-900">{t.view.title}</h1></div>
        <Badge s={b.status} />
      </div>
      <div className="card grid sm:grid-cols-2 gap-4 text-sm">
        <div><p className="text-slate-500">{t.book.steps[0]}</p><p className="font-semibold">{lang === "es" ? b.price.service_name_es : b.price.service_name_en}</p></div>
        <div><p className="text-slate-500">{t.book.steps[4]}</p><p className="font-semibold" data-testid="booking-datetime">{b.date} · {fmtTime(b.time)}</p></div>
        <div><p className="text-slate-500">{t.book.address}</p><p className="font-semibold">{[b.address.line1, b.address.line2].filter(Boolean).join(" ")}, {b.address.city} {b.address.zip}</p></div>
        <div><p className="text-slate-500">{t.book.frequency}</p><p className="font-semibold">{t.pricing[b.frequency]}</p></div>
        <div><p className="text-slate-500">{t.book.total}</p><p className="font-semibold">{money(b.price.total)}</p></div>
        <div><p className="text-slate-500">{t.view.paid} / {t.view.balance}</p><p className="font-semibold" data-testid="booking-paid-balance">{money(b.amount_paid)} / {money(b.balance_due)}</p></div>
      </div>
      <div className="flex flex-wrap gap-3">
        {b.balance_due > 0 && !["cancelled", "expired"].includes(b.status) && <button className="btn-primary" disabled={busy} onClick={pay} data-testid="booking-pay-button"><Lock size={16} />{b.payment_status === "unpaid" ? t.view.payNow : t.view.payBalance}</button>}
        {changeable && <button className="btn-outline" onClick={() => setResched(!resched)} data-testid="booking-reschedule-button">{t.view.reschedule}</button>}
        {changeable && <button className="btn-outline !text-red-600" disabled={busy} onClick={cancel} data-testid="booking-cancel-button">{t.view.cancel}</button>}
      </div>
      {resched && (
        <div className="card space-y-4">
          <SlotPicker hold={hold} onHeld={setHold} hours={b.duration_hours} />
          <button className="btn-primary" disabled={!hold || busy} onClick={saveResched} data-testid="booking-save-reschedule">{t.view.saveNew}</button>
        </div>
      )}
      {b.payments.length > 0 && (
        <div className="card">
          <h3 className="font-semibold mb-3">{t.view.receipts}</h3>
          {b.payments.map((p, i) => (
            <div key={i} className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-0">
              <span>{p.paid_at?.slice(0, 10)} · {p.kind}{p.refunded_amount > 0 ? ` (refunded ${money(p.refunded_amount)})` : ""}</span>
              <span className="flex gap-3 font-semibold">{money(p.amount)}{p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-sky-700" data-testid={`receipt-link-${i}`}>{t.view.receipt}</a>}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function QuoteView() {
  const { t } = useLang();
  useTitle(t.quote.title);
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [q, setQ] = useState(null);
  const [hold, setHold] = useState(null);
  const [opt, setOpt] = useState("full");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/quotes/${id}?token=${token}`).then((r) => setQ(r.data)).catch((e) => toast.error(errMsg(e))); }, [id, token]);
  const accept = async () => {
    setBusy(true);
    try { window.location.href = (await api.post(`/quotes/${id}/accept`, { token, hold_id: hold.hold_id, payment_option: opt })).data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!q) return <div className="p-20 grid place-items-center"><Loader2 className="animate-spin text-sky-600" /></div>;
  const open = ["sent", "accepted"].includes(q.status);
  return (
    <div className="max-w-3xl mx-auto px-4 py-12 space-y-6" data-testid="quote-view">
      <h1 className="text-4xl font-bold text-slate-900">{q.service_name}</h1>
      <div className="card space-y-2">
        <p className="text-3xl font-bold" data-testid="quote-amount">{money(q.amount)}</p>
        {q.notes && <p className="text-slate-600 whitespace-pre-line">{q.notes}</p>}
        <p className="text-sm text-slate-500">Valid until {q.valid_until} · {q.address.line1}, {q.address.city} {q.address.zip}</p>
      </div>
      {open ? (
        <div className="card space-y-5">
          <SlotPicker hold={hold} onHeld={setHold} hours={q.duration_hours} />
          <div className="grid sm:grid-cols-2 gap-3">
            {["full", "deposit"].map((o) => (
              <button key={o} type="button" onClick={() => setOpt(o)} data-testid={`quote-payment-${o}`} className={`text-left p-4 rounded-2xl border-2 ${opt === o ? "border-sky-600 bg-sky-50/60" : "border-slate-200"}`}>
                <p className="font-semibold">{t.book[o]}</p><p className="text-xl font-bold">{money(o === "full" ? q.amount : (q.amount * q.deposit_percent) / 100)}</p>
              </button>
            ))}
          </div>
          <button className="btn-primary w-full" disabled={!hold || busy} onClick={accept} data-testid="quote-accept-pay-button"><Lock size={16} />{t.book.confirm}</button>
        </div>
      ) : <p className="text-slate-600" data-testid="quote-closed">{t.status[q.status] || q.status}</p>}
    </div>
  );
}

export function PaymentResult({ cancelled }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [state, setState] = useState(null);
  const sid = params.get("session_id");
  useEffect(() => {
    if (cancelled || !sid) return;
    let n = 0;
    let stop = false;
    const poll = async () => {
      try {
        const r = (await api.get(`/payments/status/${sid}`)).data;
        setState(r);
        if (r.status === "paid" || n++ > 10 || stop) return;
      } catch { if (n++ > 10) return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sid, cancelled]);
  const bid = params.get("booking");
  const btoken = params.get("token");
  if (cancelled) return (
    <div className="max-w-xl mx-auto py-24 px-4 text-center" data-testid="payment-cancelled">
      <XCircle className="mx-auto text-amber-500" size={48} /><h1 className="mt-4 text-3xl font-bold">{t.pay.cancel}</h1><p className="mt-2 text-slate-600">{t.pay.cancelSub}</p>
      {bid && <Link to={`/booking/${bid}?token=${btoken}`} className="btn-primary mt-6" data-testid="payment-retry-link">{t.pay.retry}</Link>}
    </div>
  );
  const paid = state?.status === "paid";
  return (
    <div className="max-w-xl mx-auto py-24 px-4 text-center" data-testid="payment-success-page">
      {paid ? <CheckCircle2 className="mx-auto text-emerald-600" size={56} /> : <Loader2 className="mx-auto animate-spin text-sky-600" size={48} />}
      <h1 className="mt-4 text-3xl font-bold" data-testid="payment-status-title">{paid ? (state.kind === "balance" ? t.pay.balancePaid : t.pay.success) : state ? t.pay.pending : t.pay.checking}</h1>
      {paid && <p className="mt-2 text-slate-600">{t.pay.successSub} · <span className="font-semibold" data-testid="payment-booking-number">{state.booking_number}</span></p>}
      {state && <Link to={`/booking/${state.booking_id}?token=${state.booking_token}`} className="btn-primary mt-6" data-testid="payment-view-booking">{t.pay.view}</Link>}
    </div>
  );
}
